import React, { useState, useRef, useEffect } from 'react';
import { useGuestList } from '../hooks/useGuestList';

interface GuestRSVP {
  guestId: number;
  guestName: string;
  attending: string;
  dietaryRestrictions: string;
}

const RSVP: React.FC = () => {
  const { guests, loading, error, refetch } = useGuestList();
  const [primaryGuestId, setPrimaryGuestId] = useState<number | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameInputRef = useRef<HTMLDivElement>(null);
  const [addGuestInput, setAddGuestInput] = useState('');
  const [showAddSuggestions, setShowAddSuggestions] = useState(false);
  const addGuestInputRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    message: '',
    smsOptIn: false,
  });
  const [guestRSVPs, setGuestRSVPs] = useState<GuestRSVP[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (nameInputRef.current && !nameInputRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (addGuestInputRef.current && !addGuestInputRef.current.contains(e.target as Node)) {
        setShowAddSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Google Apps Script Web App URL
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzHEURpdMDL3Fl25K8WSnU3kogOdwRDHmbS3kiYr9mq_YOi1nyQWMWoClXuCLBj5LZ3/exec';

  const primaryGuest = guests.find(g => g.id === primaryGuestId);
  const availableGuests = guests.filter(g => !g.hasResponded);

  // Guests available to add (excluding already selected ones)
  const selectedGuestIds = guestRSVPs.map(r => r.guestId);
  const guestsToAdd = availableGuests.filter(g => !selectedGuestIds.includes(g.id));

  const nameSuggestions = nameInput.length >= 3
    ? availableGuests.filter(g => g.name.toLowerCase().includes(nameInput.toLowerCase()))
    : [];

  const addGuestSuggestions = addGuestInput.length >= 3
    ? guestsToAdd.filter(g => g.name.toLowerCase().includes(addGuestInput.toLowerCase()))
    : [];

  const handlePrimaryGuestSelect = (guest: { id: number; name: string }) => {
    setPrimaryGuestId(guest.id);
    setNameInput(guest.name);
    setShowSuggestions(false);
    setGuestRSVPs([{
      guestId: guest.id,
      guestName: guest.name,
      attending: '',
      dietaryRestrictions: '',
    }]);
    setFormData({ email: '', phone: '', message: '', smsOptIn: false });
  };

  const handleNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameInput(e.target.value);
    setPrimaryGuestId(null);
    setGuestRSVPs([]);
    setShowSuggestions(true);
  };

  const handleAddGuestSelect = (guest: { id: number; name: string }) => {
    handleAddGuest(guest.id);
    setAddGuestInput('');
    setShowAddSuggestions(false);
  };

  const handleAddGuest = (guestId: number) => {
    const guest = guests.find(g => g.id === guestId);
    if (guest) {
      setGuestRSVPs(prev => [...prev, {
        guestId: guest.id,
        guestName: guest.name,
        attending: '',
        dietaryRestrictions: '',
      }]);
    }
  };

  const handleRemoveGuest = (guestId: number) => {
    // Don't allow removing primary guest
    if (guestId === primaryGuestId) return;
    setGuestRSVPs(prev => prev.filter(r => r.guestId !== guestId));
  };

  const handleGuestRSVPChange = (guestId: number, field: keyof GuestRSVP, value: string) => {
    setGuestRSVPs(prev => prev.map(r =>
      r.guestId === guestId ? { ...r, [field]: value } : r
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (guestRSVPs.length === 0) return;

    setSubmitting(true);

    try {
      // Submit RSVP for each guest
      for (const rsvp of guestRSVPs) {
        await fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'updateRSVP',
            guestId: rsvp.guestId,
            guestName: rsvp.guestName,
            email: formData.email,
            phone: formData.phone,
            attending: rsvp.attending,
            numberOfGuests: '1',
            additionalGuests: [],
            dietaryRestrictions: rsvp.dietaryRestrictions,
            message: rsvp.guestId === primaryGuestId ? formData.message : '',
            onBehalf: rsvp.guestId !== primaryGuestId ? primaryGuest?.name : '',
            smsOptIn: formData.smsOptIn,
            timestamp: new Date().toISOString(),
          }),
        });
      }

      console.log('RSVPs Submitted for:', guestRSVPs.map(r => r.guestName).join(', '));
      setSubmitted(true);

      // Reset after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setPrimaryGuestId(null);
        setNameInput('');
        setAddGuestInput('');
        setFormData({
          email: '',
          phone: '',
          message: '',
          smsOptIn: false,
        });
        setGuestRSVPs([]);
        refetch();
      }, 5000);

    } catch (err) {
      console.error('Error submitting RSVP:', err);
      alert('There was an error submitting your RSVP. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSmsOptInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, smsOptIn: e.target.checked }));
  };

  // Check if all guests have responded
  const allGuestsResponded = guestRSVPs.every(r => r.attending !== '');

  // Count attending vs declining
  const attendingCount = guestRSVPs.filter(r => r.attending === 'yes').length;
  const decliningCount = guestRSVPs.filter(r => r.attending === 'no').length;

  return (
    <section id="rsvp" className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-center text-wedding-navy mb-3 md:mb-4">
          RSVP
        </h2>
        <p className="text-center text-gray-600 text-sm sm:text-base mb-8 md:mb-12 max-w-2xl mx-auto px-2">
          We can't wait to celebrate with you in paradise! Please let us know if you'll
          be joining us by October 1, 2026.
        </p>

        <div className="max-w-2xl mx-auto bg-wedding-cream p-4 sm:p-6 md:p-8 rounded-lg shadow-lg">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-8 md:py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-wedding-gold border-t-transparent mb-4"></div>
              <p className="text-gray-600">Loading guest list...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-8 md:py-12">
              <div className="inline-block p-3 md:p-4 bg-red-100 rounded-full mb-3 md:mb-4">
                <svg
                  className="w-10 h-10 md:w-12 md:h-12 text-red-500"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <p className="text-gray-700 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="bg-wedding-gold text-white px-6 py-2 rounded-lg font-semibold hover:bg-opacity-90"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Success State */}
          {submitted && (
            <div className="text-center py-8 md:py-12">
              <div className="inline-block p-3 md:p-4 bg-green-100 rounded-full mb-3 md:mb-4">
                <svg
                  className="w-10 h-10 md:w-12 md:h-12 text-green-500"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-wedding-navy mb-2">
                Thank You!
              </h3>
              <p className="text-gray-700 text-sm md:text-base px-2 mb-4">
                {guestRSVPs.length === 1
                  ? `RSVP received for ${guestRSVPs[0].guestName}.`
                  : `RSVPs received for ${guestRSVPs.length} guests.`}
              </p>
              {attendingCount > 0 && (
                <p className="text-green-600 text-sm">
                  {attendingCount} guest{attendingCount > 1 ? 's' : ''} attending
                </p>
              )}
              {decliningCount > 0 && (
                <p className="text-gray-500 text-sm">
                  {decliningCount} guest{decliningCount > 1 ? 's' : ''} unable to attend
                </p>
              )}
            </div>
          )}

          {/* Form */}
          {!loading && !error && !submitted && (
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              {/* Primary Guest Selection */}
              <div ref={nameInputRef}>
                <label htmlFor="guestSearch" className="block text-sm font-semibold mb-2">
                  Search Your Name *
                </label>
                <div className="relative">
                  <input
                    id="guestSearch"
                    type="text"
                    autoComplete="off"
                    value={nameInput}
                    onChange={handleNameInputChange}
                    onFocus={() => nameInput.length >= 3 && setShowSuggestions(true)}
                    placeholder="Type at least 3 characters..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wedding-gold focus:border-transparent bg-white"
                  />
                  {showSuggestions && nameSuggestions.length > 0 && (
                    <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {nameSuggestions.map(guest => (
                        <li
                          key={guest.id}
                          onMouseDown={() => handlePrimaryGuestSelect(guest)}
                          className="px-4 py-2 cursor-pointer hover:bg-wedding-cream text-sm"
                        >
                          {guest.name}
                        </li>
                      ))}
                    </ul>
                  )}
                  {showSuggestions && nameInput.length >= 3 && nameSuggestions.length === 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 px-4 py-2 text-sm text-gray-500">
                      No matching guests found
                    </div>
                  )}
                </div>
                {!primaryGuestId && nameInput.length > 0 && nameInput.length < 3 && (
                  <p className="text-xs text-gray-400 mt-1">Keep typing to see suggestions...</p>
                )}
                {availableGuests.length === 0 && guests.length > 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    All guests have already responded. If you need to update your RSVP, please contact us directly.
                  </p>
                )}
              </div>

              {/* Show form after primary guest is selected */}
              {primaryGuest && (
                <>
                  {/* Contact Information */}
                  <div className="bg-white bg-opacity-50 p-4 rounded-lg space-y-4">
                    <h4 className="font-semibold text-wedding-navy">Contact Information</h4>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wedding-gold focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wedding-gold focus:border-transparent"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="smsOptIn"
                        name="smsOptIn"
                        checked={formData.smsOptIn}
                        onChange={handleSmsOptInChange}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-wedding-gold focus:ring-wedding-gold"
                      />
                      <label htmlFor="smsOptIn" className="text-sm text-gray-700">
                        I'd like to receive text message updates about the wedding (schedule,
                        venue, and logistics reminders). Message and data rates may apply. Reply
                        STOP to unsubscribe at any time.
                      </label>
                    </div>
                  </div>

                  {/* Guest RSVPs */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-wedding-navy">
                      Guest Responses ({guestRSVPs.length} guest{guestRSVPs.length > 1 ? 's' : ''})
                    </h4>

                    {guestRSVPs.map((rsvp, index) => (
                      <div
                        key={rsvp.guestId}
                        className="bg-white p-4 rounded-lg border-2 border-gray-200"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <span className="font-semibold text-wedding-navy">{rsvp.guestName}</span>
                            {rsvp.guestId === primaryGuestId && (
                              <span className="ml-2 text-xs bg-wedding-gold text-white px-2 py-0.5 rounded">You</span>
                            )}
                          </div>
                          {rsvp.guestId !== primaryGuestId && (
                            <button
                              type="button"
                              onClick={() => handleRemoveGuest(rsvp.guestId)}
                              className="text-gray-400 hover:text-red-500 text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Will {rsvp.guestId === primaryGuestId ? 'you' : rsvp.guestName.split(' ')[0]} be attending? *
                            </label>
                            <select
                              required
                              value={rsvp.attending}
                              onChange={(e) => handleGuestRSVPChange(rsvp.guestId, 'attending', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wedding-gold focus:border-transparent bg-white text-sm"
                            >
                              <option value="">Please select</option>
                              <option value="yes">Joyfully accepts</option>
                              <option value="no">Regretfully declines</option>
                            </select>
                          </div>

                          {rsvp.attending === 'yes' && (
                            <div>
                              <label className="block text-sm font-medium mb-1">
                                Dietary Restrictions
                              </label>
                              <input
                                type="text"
                                value={rsvp.dietaryRestrictions}
                                onChange={(e) => handleGuestRSVPChange(rsvp.guestId, 'dietaryRestrictions', e.target.value)}
                                placeholder="Any allergies or dietary needs?"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wedding-gold focus:border-transparent text-sm"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Another Guest */}
                  {guestsToAdd.length > 0 && (
                    <div className="bg-white bg-opacity-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold mb-2">
                        Respond for another guest?
                      </label>
                      <div ref={addGuestInputRef} className="relative">
                        <input
                          type="text"
                          autoComplete="off"
                          value={addGuestInput}
                          onChange={(e) => {
                            setAddGuestInput(e.target.value);
                            setShowAddSuggestions(true);
                          }}
                          onFocus={() => addGuestInput.length >= 3 && setShowAddSuggestions(true)}
                          placeholder="Type at least 3 characters..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wedding-gold focus:border-transparent bg-white"
                        />
                        {showAddSuggestions && addGuestSuggestions.length > 0 && (
                          <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                            {addGuestSuggestions.map(guest => (
                              <li
                                key={guest.id}
                                onMouseDown={() => handleAddGuestSelect(guest)}
                                className="px-4 py-2 cursor-pointer hover:bg-wedding-cream text-sm"
                              >
                                {guest.name}
                              </li>
                            ))}
                          </ul>
                        )}
                        {showAddSuggestions && addGuestInput.length >= 3 && addGuestSuggestions.length === 0 && (
                          <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 px-4 py-2 text-sm text-gray-500">
                            No matching guests found
                          </div>
                        )}
                      </div>
                      {addGuestInput.length > 0 && addGuestInput.length < 3 && (
                        <p className="text-xs text-gray-400 mt-1">Keep typing to see suggestions...</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        You can respond on behalf of family members or guests in your party
                      </p>
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold mb-2">
                      Message to the Couple
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Share your excitement or any questions..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wedding-gold focus:border-transparent"
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting || !allGuestsResponded}
                    className="w-full bg-wedding-gold text-white py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {submitting
                      ? 'Submitting...'
                      : guestRSVPs.length === 1
                        ? 'Submit RSVP'
                        : `Submit ${guestRSVPs.length} RSVPs`}
                  </button>

                  {!allGuestsResponded && guestRSVPs.length > 0 && (
                    <p className="text-sm text-center text-gray-500">
                      Please select attending status for all guests
                    </p>
                  )}
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default RSVP;
